import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { Loader2 } from "lucide-react";

const SIDE_IMG = "https://images.unsplash.com/photo-1747848214256-34a8cd21abac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxzdHlsaXNoJTIwb2xkZXIlMjBibGFjayUyMHdvbWFuJTIwZmFzaGlvbnxlbnwwfHx8fDE3ODg2Mjg3ODJ8MA&ixlib=rb-4.1.0&q=85";

export default function Register() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/onboarding");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Please choose a password of at least 6 characters.");
      return;
    }
    setLoading(true);
    try {
      await register(name, email, password);
      navigate("/onboarding");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-sand text-ink">
      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-16 order-2 lg:order-1">
        <Link to="/" className="font-serif text-2xl mb-16" data-testid="brand-logo">Élanora</Link>
        <div className="max-w-md">
          <p className="eyebrow mb-4">Begin your profile</p>
          <h1 className="text-4xl sm:text-5xl tracking-tight">Let's design your style</h1>
          <p className="mt-4 text-ink/70">Takes about ten minutes. Your first palette and picks are waiting on the other side.</p>
          <form onSubmit={submit} className="mt-10 space-y-8">
            <div>
              <label className="block text-sm text-ink/60 mb-2">First name</label>
              <input
                type="text" required value={name} onChange={(e) => setName(e.target.value)}
                data-testid="register-name-input"
                className="w-full bg-transparent border-b border-ink/40 focus:border-emerald outline-none py-3 text-lg"
              />
            </div>
            <div>
              <label className="block text-sm text-ink/60 mb-2">Email</label>
              <input
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="register-email-input"
                className="w-full bg-transparent border-b border-ink/40 focus:border-emerald outline-none py-3 text-lg"
              />
            </div>
            <div>
              <label className="block text-sm text-ink/60 mb-2">Password</label>
              <input
                type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="register-password-input"
                className="w-full bg-transparent border-b border-ink/40 focus:border-emerald outline-none py-3 text-lg"
              />
            </div>
            {error && <p className="text-burgundy text-sm" data-testid="register-error">{error}</p>}
            <button
              type="submit" disabled={loading} data-testid="register-submit"
              className="w-full rounded-full bg-emerald text-white py-4 text-base hover:bg-emerald/90 transition-colors duration-300 disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />}
              Create my profile
            </button>
          </form>
          <p className="mt-8 text-ink/60">
            Already have an account?{" "}
            <Link to="/login" className="text-emerald underline underline-offset-4" data-testid="link-login">
              Sign in
            </Link>
          </p>
        </div>
      </div>
      <div className="hidden lg:block relative order-1 lg:order-2">
        <img src={SIDE_IMG} alt="Stylish woman portrait" className="absolute inset-0 w-full h-full object-cover" />
      </div>
    </div>
  );
}
